/**
 * VCloud Expanded Row Helpers
 */
import type { TotalsResult } from './types'

// ============================================================================
// VM Config Parser
// ============================================================================

export const parseVMConfig = (name: string) => {
    if (!name) return { cores: 0, ram: 0 }
    const match = name.match(/(\d+)-(\d+)$/)
    if (match) {
        return {
            cores: parseInt(match[1]),
            ram: parseInt(match[2]),
        }
    }
    return { cores: 0, ram: 0 }
}

// ============================================================================
// Type Detection
// ============================================================================

export const isStorageType = (type?: string, name?: string): boolean => {
    const t = type?.toLowerCase() || ''
    const n = name?.toLowerCase() || ''
    return (
        t.includes('storage') ||
        t.includes('disk') ||
        t.includes('ssd') ||
        t.includes('hdd') ||
        n.includes('storage') ||
        n.includes('disk') ||
        n.includes('ssd') ||
        n.includes('hdd')
    )
}

export const isPublicIPType = (type?: string, name?: string): boolean => {
    const t = type?.toLowerCase() || ''
    const n = name?.toLowerCase() || ''
    if (t.includes('floating') || n.includes('floating')) return false
    return (
        (t.includes('public') && t.includes('ip')) ||
        (n.includes('public') && n.includes('ip')) ||
        t === 'public-ip' ||
        t === 'publicip' ||
        n === 'public-ip' ||
        n === 'publicip'
    )
}

export const isFloatingIPType = (type?: string, name?: string): boolean => {
    const t = type?.toLowerCase() || ''
    const n = name?.toLowerCase() || ''
    return t.includes('floating') || n.includes('floating')
}

export const isBandwidthType = (type?: string, name?: string): boolean => {
    const t = type?.toLowerCase() || ''
    const n = name?.toLowerCase() || ''
    return (
        t.includes('bandwidth') ||
        t.includes('bw') ||
        n.includes('bandwidth') ||
        n.includes('bw')
    )
}

export const getServiceType = (
    item: any
): 'vm_instance' | 'vm_lb' | 'other_service' => {
    const type = item.type?.toLowerCase() || ''
    const name = item.name?.toLowerCase() || ''
    if (type === 'vm-instance' || name.includes('instance'))
        return 'vm_instance'
    if (
        type === 'vm-lb' ||
        name.includes('lb') ||
        name.includes('load') ||
        name.includes('balancer')
    )
        return 'vm_lb'
    return 'other_service'
}

// ============================================================================
// Totals Calculator
// ============================================================================

export const calculateTotals = (items: any[]): TotalsResult => {
    return items.reduce(
        (totals, item) => {
            const serviceType = getServiceType(item)
            const quantity = item.commitment_value || 0
            const config = parseVMConfig(item.name || '')

            if (serviceType === 'vm_instance' || serviceType === 'vm_lb') {
                totals.vCores += config.cores * quantity
                totals.ram += config.ram * quantity
            } else {
                if (isStorageType(item.type, item.name))
                    totals.storage += quantity
                if (isPublicIPType(item.type, item.name))
                    totals.publicIP += quantity
                if (isFloatingIPType(item.type, item.name))
                    totals.floatingIP += quantity
                if (isBandwidthType(item.type, item.name))
                    totals.bandwidth += quantity
            }

            const itemPrice =
                item.commitment_price_total ||
                (item.commitment_price_per_unit || 0) * quantity
            totals.price += itemPrice
            return totals
        },
        {
            vCores: 0,
            ram: 0,
            storage: 0,
            publicIP: 0,
            floatingIP: 0,
            bandwidth: 0,
            price: 0,
        }
    )
}

export const calculateTypeTotals = (items: any[], typeKey: string) => {
    return items.reduce(
        (totals, item) => {
            const quantity = item.commitment_value || 0
            const config = parseVMConfig(item.name || '')
            if (typeKey === 'vm_instance' || typeKey === 'vm_lb') {
                totals.vCores += config.cores * quantity
                totals.ram += config.ram * quantity
            } else {
                if (isStorageType(item.type, item.name))
                    totals.storage += quantity
                if (isPublicIPType(item.type, item.name))
                    totals.publicIP += quantity
                if (isFloatingIPType(item.type, item.name))
                    totals.floatingIP += quantity
                if (isBandwidthType(item.type, item.name))
                    totals.bandwidth += quantity
            }
            return totals
        },
        {
            vCores: 0,
            ram: 0,
            storage: 0,
            publicIP: 0,
            floatingIP: 0,
            bandwidth: 0,
        }
    )
}
